import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

export type UploadStatus = 'queued' | 'initializing' | 'uploading' | 'paused' | 'completed' | 'failed' | 'cancelled';

export interface UploadFileItem {
  fileId: string; // Unique identifier (file name + timestamp)
  file: File;
  uploadUrl?: string;
  filePath?: string;
  fileRecordId?: string; // Backend record ID from init API
  chunkSize?: number;
  progress: number;
  status: UploadStatus;
  bytesUploaded: number;
  totalBytes: number;
  error?: string;
  abortController?: AbortController;
  retryCount?: number;
  streamThroughBackend?: boolean; 
  sessionId?: string; // Session ID for resume functionality
  metadata?: {
    folderId?: number;
    libraryType?: number;
    departmentId?: number;
    titleEn?: string;
    titleAr?: string;
    eventId?: number;
    eventDate?: string;
    eventStartDate?: string;
    eventEndDate?: string;
    locationId?: number;
    newLocation?: string;
    tagIds?: number[];
    newTags?: string[];
    newEvent?: string;
    isMarkSecure?: boolean;
    userEmails?: string;
    shareType?: 'internal' | 'external';
    shareUserIds?: number[];
    shareEmails?: string[];
    shareExpireOn?: string | null;
    userId?: number;
    fileCreationDate?: string;
    /** Optional approval workflow id (Enable Workflow on upload). */
    workflowId?: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class UploadManagerService {
  private uploadsMap = new Map<string, UploadFileItem>();
  private uploadsSubject = new BehaviorSubject<Map<string, UploadFileItem>>(new Map());
  private currentUploadingId: string | null = null;
  private readonly MAX_RETRIES = 3;

  // Parallel upload support
  private activeUploadingIds = new Set<string>();
  private concurrentChunksMap = new Map<string, number>(); // Track concurrent chunks per file
  maxConcurrentFiles: number = 1; // Default: sequential
  maxConcurrentChunksPerFile: number = 1; // Default: sequential
  currentUploadSpeed: number = 0; // Bytes per second
  connectionSpeed: 'slow' | 'medium' | 'fast' | 'veryFast' = 'slow';

  /** Emitted when paused uploads are re-queued and the resumable processor should run */
  private readonly processingRequestedSubject = new Subject<void>();
  readonly processingRequested$ = this.processingRequestedSubject.asObservable();

  /**
   * Observable stream of all uploads
   */
  get uploads$(): Observable<UploadFileItem[]> {
    return this.uploadsSubject.pipe(
      map(uploadsMap => Array.from(uploadsMap.values()))
    );
  }

  /**
   * Observable stream of active uploads (not completed, failed, or cancelled)
   */
  get activeUploads$(): Observable<UploadFileItem[]> {
    return this.uploads$.pipe(
      map(uploads => uploads.filter(u => 
        u.status !== 'completed' && u.status !== 'failed' && u.status !== 'cancelled'
      ))
    );
  }

  /**
   * Get upload by ID
   */
  getUpload(fileId: string): UploadFileItem | undefined {
    return this.uploadsMap.get(fileId);
  }

  /**
   * Get all uploads
   */
  getAllUploads(): UploadFileItem[] {
    return Array.from(this.uploadsMap.values());
  }

  /**
   * Get active uploads count
   */
  getActiveUploadsCount(): number {
    return Array.from(this.uploadsMap.values()).filter(u => 
      u.status !== 'completed' && u.status !== 'failed' && u.status !== 'cancelled'
    ).length;
  }

  /** True while files are queued or actively uploading (excludes paused). */
  hasInProgressUploads(): boolean {
    return Array.from(this.uploadsMap.values()).some(
      u =>
        u.status === 'queued' ||
        u.status === 'initializing' ||
        u.status === 'uploading'
    );
  }

  /**
   * Add files to queue
   */
  addFiles(files: File[], metadata?: UploadFileItem['metadata'], perFileMetadata?: UploadFileItem['metadata'][]): string[] {
    const fileIds: string[] = [];
    
    files.forEach((file, index) => {
      // Check for duplicates by file properties
      const isDuplicate = Array.from(this.uploadsMap.values()).some(upload => 
        upload.file.name === file.name &&
        upload.file.size === file.size &&
        upload.file.lastModified === file.lastModified &&
        upload.status !== 'cancelled' &&
        upload.status !== 'failed'
      );

      if (isDuplicate) {
        console.log(`[UPLOAD] Skipping duplicate file: ${file.name}`);
        return;
      }

      const fileId = this.generateFileId(file);
      const itemMetadata = perFileMetadata && perFileMetadata[index] 
        ? perFileMetadata[index] 
        : (metadata || {});

      const uploadItem: UploadFileItem = {
        fileId,
        file,
        progress: 0,
        status: 'queued',
        bytesUploaded: 0,
        totalBytes: file.size,
        retryCount: 0,
        metadata: itemMetadata
      };

      this.uploadsMap.set(fileId, uploadItem);
      fileIds.push(fileId);
    });

    this.emitUpdate();
    return fileIds;
  }

  /**
   * Add resumable session item (bypass duplicate name/size checks, unique by sessionId)
   */
  addResumableSession(file: File, sessionId: string, metadata?: UploadFileItem['metadata']): string | null {
    if (!sessionId) return null;

    const existing = Array.from(this.uploadsMap.values()).some(upload =>
      upload.sessionId === sessionId
    );
    if (existing) {
      return null;
    }

    const fileId = `resumable-${sessionId}`;
    const uploadItem: UploadFileItem = {
      fileId,
      file,
      progress: 0,
      status: 'queued',
      bytesUploaded: 0,
      totalBytes: file.size,
      retryCount: 0,
      sessionId,
      metadata: metadata || {}
    };

    this.uploadsMap.set(fileId, uploadItem);
    this.emitUpdate();
    return fileId;
  }

  /**
   * Update upload status
   */
  updateStatus(fileId: string, status: UploadStatus, error?: string): void {
    const upload = this.uploadsMap.get(fileId);
    if (!upload) return;

    upload.status = status;
    if (error) {
      upload.error = error;
    } else if (status === 'completed' || status === 'failed' || status === 'cancelled') {
      upload.error = undefined;
    }

    // If cancelled, abort immediately
    if (status === 'cancelled') {
      if (upload.abortController) {
        upload.abortController.abort();
      }
      // Clear current uploading if this was the active upload
      if (this.currentUploadingId === fileId) {
        this.currentUploadingId = null;
      }
    }

    this.emitUpdate();
  }

  /**
   * Update upload progress
   */
  updateProgress(fileId: string, bytesUploaded: number): void {
    const upload = this.uploadsMap.get(fileId);
    if (!upload) return;

    upload.bytesUploaded = bytesUploaded;
    upload.progress = upload.totalBytes > 0 
      ? Math.min(100, (bytesUploaded / upload.totalBytes) * 100)
      : 0;

    this.emitUpdate();
  }

  /**
   * Update upload session data (from init API)
   */
  updateSessionData(fileId: string, data: {
    uploadUrl?: string;
    filePath?: string;
    fileRecordId?: string;
    chunkSize?: number;
    sessionId?: string;
    bytesUploaded?: number;
  }): void {
    // If bytesUploaded is provided, update progress as well
    if (data.bytesUploaded !== undefined) {
      this.updateProgress(fileId, data.bytesUploaded);
    }
    const upload = this.uploadsMap.get(fileId);
    if (!upload) return;

    if (data.uploadUrl) upload.uploadUrl = data.uploadUrl;
    if (data.filePath) upload.filePath = data.filePath;
    if (data.fileRecordId) upload.fileRecordId = data.fileRecordId;
    if (data.chunkSize) upload.chunkSize = data.chunkSize;
    if (data.sessionId) upload.sessionId = data.sessionId;
    if (data.bytesUploaded !== undefined) upload.bytesUploaded = data.bytesUploaded;

    this.emitUpdate();
  }

  /**
   * Update upload metadata (for queued items before initialize)
   */
  updateMetadata(fileId: string, metadata: UploadFileItem['metadata']): void {
    const upload = this.uploadsMap.get(fileId);
    if (!upload) return;
    upload.metadata = {
      ...(upload.metadata || {}),
      ...(metadata || {})
    };
    this.emitUpdate();
  }

  /**
   * Set current uploading file (only one at a time) - kept for backward compatibility
   */
  setCurrentUploading(fileId: string | null): void {
    this.currentUploadingId = fileId;
    if (fileId) {
      this.activeUploadingIds.add(fileId);
    } else {
      this.activeUploadingIds.clear();
    }
  }

  /**
   * Get current uploading file ID - kept for backward compatibility
   */
  getCurrentUploadingId(): string | null {
    return this.currentUploadingId;
  }

  /**
   * Add file to active uploading set (for parallel uploads)
   */
  addActiveUploading(fileId: string): void {
    this.activeUploadingIds.add(fileId);
    if (this.currentUploadingId === null) {
      this.currentUploadingId = fileId; // Set first one as primary for backward compatibility
    }
  }

  /**
   * Remove file from active uploading set
   */
  removeActiveUploading(fileId: string): void {
    this.activeUploadingIds.delete(fileId);
    if (this.currentUploadingId === fileId) {
      // Set next active upload as primary, or null if none
      this.currentUploadingId = this.activeUploadingIds.size > 0 
        ? Array.from(this.activeUploadingIds)[0] 
        : null;
    }
  }

  /**
   * Get all active uploading file IDs
   */
  getActiveUploadingIds(): string[] {
    return Array.from(this.activeUploadingIds);
  }

  /**
   * Check if file can start uploading (based on concurrent file limit)
   */
  canStartUpload(fileId: string): boolean {
    // If we haven't set concurrency limits yet, use old behavior (sequential)
    if (this.maxConcurrentFiles === 1) {
      return this.currentUploadingId === null || this.currentUploadingId === fileId;
    }
    // Check if we have room for more concurrent uploads
    return this.activeUploadingIds.size < this.maxConcurrentFiles || 
           this.activeUploadingIds.has(fileId);
  }

  /**
   * Increment concurrent chunk count for a file
   */
  incrementConcurrentChunks(fileId: string): void {
    const current = this.concurrentChunksMap.get(fileId) || 0;
    this.concurrentChunksMap.set(fileId, current + 1);
  }

  /**
   * Decrement concurrent chunk count for a file
   */
  decrementConcurrentChunks(fileId: string): void {
    const current = this.concurrentChunksMap.get(fileId) || 0;
    if (current > 0) {
      this.concurrentChunksMap.set(fileId, current - 1);
    }
  }

  /**
   * Get concurrent chunks count for a file
   */
  getConcurrentChunksCount(fileId: string): number {
    return this.concurrentChunksMap.get(fileId) || 0;
  }

  /**
   * Check if file can upload more chunks (based on concurrent chunk limit)
   */
  canUploadMoreChunks(fileId: string): boolean {
    const currentChunks = this.getConcurrentChunksCount(fileId);
    return currentChunks < this.maxConcurrentChunksPerFile;
  }

  /**
   * Update upload speed and adjust concurrency limits
   */
  updateUploadSpeed(bytesPerSecond: number): void {
    this.currentUploadSpeed = bytesPerSecond;
    
    // Classify connection speed
    const kbps = bytesPerSecond / 1024; // Convert to KB/s
    const mbps = kbps / 1024; // Convert to MB/s
    
    if (mbps < 1) {
      // Slow: < 1 Mbps — keep API load low (1–3 files)
      this.connectionSpeed = 'slow';
      this.maxConcurrentFiles = 2;
      this.maxConcurrentChunksPerFile = 2;
    } else if (mbps < 5) {
      // Medium: 1–5 Mbps (4–6 files)
      this.connectionSpeed = 'medium';
      this.maxConcurrentFiles = 3;
      this.maxConcurrentChunksPerFile = 3;
    } else if (mbps < 20) {
      // Fast: 5–20 Mbps (up to 7 files)
      this.connectionSpeed = 'fast';
      this.maxConcurrentFiles = 4;
      this.maxConcurrentChunksPerFile = 4;
    } else {
      // Very fast: cap at 7 files — avoid thundering herd on deployed API
      this.connectionSpeed = 'veryFast';
      this.maxConcurrentFiles = 5;
      this.maxConcurrentChunksPerFile = 5;
    }
    
    console.log(`[SPEED] Detected: ${this.connectionSpeed} (${mbps.toFixed(2)} Mbps) - Files: ${this.maxConcurrentFiles}, Chunks/file: ${this.maxConcurrentChunksPerFile}`);
  }

  /**
   * Get next queued file to upload (supports parallel uploads)
   */
  getNextQueuedFile(): UploadFileItem | null {
    // If using sequential mode (old behavior)
    if (this.maxConcurrentFiles === 1 && this.currentUploadingId !== null) {
      return null; // Already uploading
    }

    // Check if we have room for more concurrent uploads
    if (this.activeUploadingIds.size >= this.maxConcurrentFiles) {
      return null; // At max concurrent uploads
    }

    const uploads = Array.from(this.uploadsMap.values());
    
    // Find queued file that's not already being uploaded
    const queued = uploads.find(u => 
      u.status === 'queued' && 
      !this.activeUploadingIds.has(u.fileId)
    );
    if (queued) {
      return queued;
    }

    // Check for failed files that can be retried
    const failed = uploads.find(u => 
      u.status === 'failed' && 
      (u.retryCount || 0) < this.MAX_RETRIES &&
      !this.activeUploadingIds.has(u.fileId)
    );
    return failed || null;
  }

  /**
   * Pause upload
   */
  pauseUpload(fileId: string): void {
    const upload = this.uploadsMap.get(fileId);
    if (!upload) return;
    if (!['uploading', 'initializing', 'queued'].includes(upload.status)) return;

    upload.status = 'paused';
    if (upload.abortController) {
      // Don't abort, just mark as paused - let current chunk complete
      // The upload logic will check status after chunk completes
    }

    // Clear current uploading if this was active
    if (this.currentUploadingId === fileId) {
      this.currentUploadingId = null;
    }
    this.removeActiveUploading(fileId);

    this.emitUpdate();
  }

  /**
   * Pause all uploads (uploading, initializing, queued)
   */
  pauseAllUploads(): void {
    let pausedAny = false;
    this.uploadsMap.forEach((upload, fileId) => {
      if (['uploading', 'initializing', 'queued'].includes(upload.status)) {
        upload.status = 'paused';
        this.removeActiveUploading(fileId);
        pausedAny = true;
      }
    });
    if (pausedAny) {
      this.currentUploadingId = null;
      this.activeUploadingIds.clear();
    }
    this.emitUpdate();
  }

  /**
   * Ask the resumable upload processor to pick up queued work (e.g. after resume from indicator).
   */
  requestQueueProcessing(): void {
    this.processingRequestedSubject.next();
  }

  /**
   * Resume upload
   */
  resumeUpload(fileId: string): void {
    const upload = this.uploadsMap.get(fileId);
    if (!upload || upload.status !== 'paused') return;

    upload.error = undefined;
    upload.abortController = new AbortController();
    upload.status = 'queued';

    this.emitUpdate();
    this.requestQueueProcessing();
  }

  /**
   * Resume multiple paused uploads and notify the queue processor once.
   */
  resumePausedUploads(fileIds: string[]): number {
    let resumedCount = 0;
    fileIds.forEach(fileId => {
      const upload = this.uploadsMap.get(fileId);
      if (!upload || upload.status !== 'paused') {
        return;
      }
      upload.error = undefined;
      upload.abortController = new AbortController();
      upload.status = 'queued';
      resumedCount += 1;
    });
    if (resumedCount > 0) {
      this.emitUpdate();
      this.requestQueueProcessing();
    }
    return resumedCount;
  }

  /**
   * Cancel upload - immediate termination
   */
  cancelUpload(fileId: string): void {
    const upload = this.uploadsMap.get(fileId);
    if (!upload) return;

    // Abort immediately
    if (upload.abortController) {
      upload.abortController.abort();
    }

    upload.status = 'cancelled';
    upload.error = undefined;

    // Clear from active uploads
    this.removeActiveUploading(fileId);
    
    // Clear concurrent chunks tracking
    this.concurrentChunksMap.delete(fileId);

    this.emitUpdate();
  }

  /**
   * Remove upload from queue
   */
  removeUpload(fileId: string): void {
    const upload = this.uploadsMap.get(fileId);
    if (upload?.abortController) {
      upload.abortController.abort();
    }

    this.uploadsMap.delete(fileId);

    // Clear from active uploads
    this.removeActiveUploading(fileId);
    
    // Clear concurrent chunks tracking
    this.concurrentChunksMap.delete(fileId);

    this.emitUpdate();
  }

  /**
   * Increment retry count
   */
  incrementRetryCount(fileId: string): void {
    const upload = this.uploadsMap.get(fileId);
    if (upload) {
      upload.retryCount = (upload.retryCount || 0) + 1;
      this.emitUpdate();
    }
  }

  /**
   * Get completed uploads (for Submit button check)
   */
  getCompletedUploads(): UploadFileItem[] {
    return Array.from(this.uploadsMap.values()).filter(u => u.status === 'completed');
  }

  /**
   * Check if all uploads are completed and verified
   */
  areAllUploadsCompleted(): boolean {
    const uploads = Array.from(this.uploadsMap.values());
    if (uploads.length === 0) return false;

    return uploads.every(u => 
      u.status === 'completed' || 
      u.status === 'cancelled' || 
      u.status === 'failed'
    );
  }

  /**
   * Check if all active uploads are completed (excluding cancelled)
   */
  areAllActiveUploadsCompleted(): boolean {
    const activeUploads = Array.from(this.uploadsMap.values()).filter(u => 
      u.status !== 'cancelled'
    );
    
    if (activeUploads.length === 0) return false;

    return activeUploads.every(u => u.status === 'completed');
  }

  /**
   * Clear completed uploads
   */
  clearCompleted(): void {
    const toRemove: string[] = [];
    this.uploadsMap.forEach((upload, fileId) => {
      if (upload.status === 'completed' || upload.status === 'failed') {
        toRemove.push(fileId);
      }
    });
    toRemove.forEach(fileId => this.removeUpload(fileId));
  }

  /**
   * Clear all uploads
   */
  clearAll(): void {
    this.uploadsMap.forEach((upload) => {
      if (upload.abortController) {
        upload.abortController.abort();
      }
    });
    this.uploadsMap.clear();
    this.currentUploadingId = null;
    this.activeUploadingIds.clear();
    this.concurrentChunksMap.clear();
    this.emitUpdate();
  }

  /**
   * Generate unique file ID
   */
  private generateFileId(file: File): string {
    return `${file.name}_${file.size}_${file.lastModified}_${Date.now()}`;
  }

  /**
   * Emit update to subscribers
   */
  private emitUpdate(): void {
    this.uploadsSubject.next(new Map(this.uploadsMap));
  }
}

